import { useCallback, useEffect } from 'react';

import { ThemedLite } from '@opendoor/bricks/theme/ODThemeLite';
import { ErrorResponse, isRouteErrorResponse, useRouteError } from '@remix-run/react';

import { RAILS_ENV } from 'shared/helpers/globals';
import {
  ErrorPageContent,
  useTrackGenericErrorPageViewEvent,
} from 'shared/routes/errors/GenericErrorPage';
import { NotFoundPage } from 'shared/routes/errors/NotFoundPage';

import { useObservability } from 'platform/observability';

import { TeamError } from './Error';

/**
 * This component is responsible for tracking the error in observability and rendering the appropriate error page.
 * NOTE: This component should ONLY be used as a top-level error boundary in the application.
 * @returns The error page to render.
 */
export const RemixErrorHandler = () => {
  const error = useRouteError();
  const { trackError } = useObservability();
  const { trackGenericErrorPageView } = useTrackGenericErrorPageViewEvent();

  if (RAILS_ENV !== 'production') {
    // eslint-disable-next-line no-console -- dev only consoles
    error && console.error(error);
  }

  /**
   * Tracks the error in observability with team data.
   */
  const trackBoundaryError = useCallback(
    (reason: string, error: ErrorResponse | TeamError | Error) => {
      trackGenericErrorPageView(reason, 'system-error', { ...error });
      // eslint-disable-next-line no-restricted-syntax -- error boundary triggers top level error alerting
      trackError(reason, error instanceof Error ? error : new Error(reason), { ...error });
    },
    [trackError, trackGenericErrorPageView],
  );

  const routeError = isRouteErrorResponse(error) ? error : undefined;
  useEffect(() => {
    if (routeError) {
      trackBoundaryError(
        '[RouteError] ' + `${routeError.status} - ${routeError.statusText}`,
        routeError,
      );
    } else if (error instanceof TeamError) {
      trackBoundaryError('[TeamError] ' + error.message, error);
    } else if (error instanceof Error) {
      trackBoundaryError('[Error] ' + error.message, error);
    } else if (error instanceof Response) {
      trackBoundaryError(
        '[ResponseThrown] ' + `${error.status} - ${error.statusText ?? 'Failed to redirect'}`,
        new Error('failed-to-redirect'),
      );
    } else {
      trackBoundaryError(
        '[UnknownError]' + String(error),
        new TeamError(new Error(String(error)), 'consumer-platform', 'system-error'),
      );
    }
  }, [error, routeError, trackBoundaryError]);

  return (
    <ThemedLite theme="novo">
      {routeError?.status === 404 ? (
        // Renders 404 page if the route is not found
        <NotFoundPage />
      ) : (
        // Falls back to showing the generic error
        <ErrorPageContent />
      )}
    </ThemedLite>
  );
};
